import { useState } from "react";
import { useTranslation } from "react-i18next";
import { httpsCallable } from "firebase/functions";
import { functions } from "../services/firebase";
import Card from "react-bootstrap/esm/Card";
import Form from "react-bootstrap/esm/Form";

type Channel = "email" | "whatsapp";

interface MyPlanConsentProps {
    token: string;
    consents: Record<Channel, boolean>;
    hasEmail: boolean;
    hasPhone: boolean;
    onChange: (consents: Record<Channel, boolean>) => void;
}

/**
 * The preacher's own consent to reminders per channel (GOD-35): given or
 * withdrawn here at any time, recorded with time and source "self-service".
 */
export default function MyPlanConsent({ token, consents, hasEmail, hasPhone, onChange }: MyPlanConsentProps) {
    const { t } = useTranslation();
    const [busy, setBusy] = useState<Channel | null>(null);
    const [error, setError] = useState("");

    async function toggle(channel: Channel, given: boolean) {
        setBusy(channel);
        setError("");
        try {
            const call = httpsCallable<{ token: string; channel: Channel; given: boolean }, { given: boolean }>(functions, "setMyConsent");
            await call({ token, channel, given });
            onChange({ ...consents, [channel]: given });
        } catch {
            setError(t("errors.anErrorOccurred") as string);
        }
        setBusy(null);
    }

    return (
        <Card className="mb-3" data-testid="my-plan-consent">
            <Card.Body>
                <h5>{t("myPlan.consentHeading")}</h5>
                <p className="text-muted small">{t("myPlan.consentIntro")}</p>
                <Form.Check
                    type="switch"
                    id="my-consent-email"
                    label={t("myPlan.consentEmail")}
                    checked={consents.email}
                    disabled={busy !== null || !hasEmail}
                    onChange={(e) => toggle("email", e.target.checked)}
                />
                <Form.Check
                    type="switch"
                    id="my-consent-whatsapp"
                    label={t("myPlan.consentWhatsApp")}
                    checked={consents.whatsapp}
                    disabled={busy !== null || !hasPhone}
                    onChange={(e) => toggle("whatsapp", e.target.checked)}
                />
                {(!hasEmail || !hasPhone) && <div className="small text-muted mt-1">{t("myPlan.consentMissingContact")}</div>}
                {error && <div className="small text-danger mt-1">{error}</div>}
            </Card.Body>
        </Card>
    );
}
